import React, { useCallback, useRef } from 'react';
import { Input } from 'antd';
import type { InputProps } from 'antd';
import { normalizeEmail } from '../../utils/normalizeNumber';

/**
 * メールアドレス入力用のカスタムInput
 *
 * @description
 * 全角英数字・記号（@、.など）を自動的に半角に変換します。
 * メールアドレスの入力に使用します。
 *
 * @example
 * <Form.Item
 *   name="email"
 *   rules={[{ type: 'email', message: '正しいメールアドレスを入力してください' }]}
 * >
 *   <EmailInput placeholder="example@example.com" />
 * </Form.Item>
 */
export const EmailInput: React.FC<InputProps> = ({ onChange, ...props }) => {
  const isComposingRef = useRef(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nativeEvent = e.nativeEvent as InputEvent;
      const isComposing = nativeEvent?.isComposing ?? false;

      if (isComposingRef.current || isComposing) {
        onChange?.(e);
        return;
      }

      const originalValue = e.target.value;
      const normalizedValue = normalizeEmail(originalValue);

      if (normalizedValue === originalValue) {
        onChange?.(e);
        return;
      }

      // 正規化した値で新しいイベントを作成
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: normalizedValue,
        },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange?.(newEvent);
    },
    [onChange]
  );

  return (
    <Input
      {...props}
      onChange={handleChange}
      onCompositionStart={(event) => {
        isComposingRef.current = true;
        props.onCompositionStart?.(event);
      }}
      onCompositionEnd={(event) => {
        isComposingRef.current = false;
        props.onCompositionEnd?.(event);
      }}
    />
  );
};

export default EmailInput;
